import { Checkbox, Grid, Stack, useTheme } from "@mui/material";
import { useEffect, useMemo, useRef } from "react";
import { makeTintedBackgroundFromColor } from "@entrilia/shared/utilities/colorHelper.ts";
import type { CustomMeasureReturnType } from "../../customMeasure/hooks/useCustomMeasure";
import type { FormulaNode } from "../../customMeasure/utilities/fillNodes";
import { findNodeByTokenData } from "../../customMeasure/utilities/findNodeByTokenData";
import type { ChartOfAccountsContextItem } from "../contexts/ChartOfAccountsContext";
import { useChartOfAccountsContext } from "../contexts/ChartOfAccountsContext";
import { getFunctionPropertyItemUniqueId } from "../helpers/chartOfAccountsContextHelper";
import PropertyItemContainer from "./PropertyItemContainer";
import { PropertyName } from "./PropertyName";
import VariablePropertyItem from "./VariablePropertyItem";

interface Props {
  formulaNode: FormulaNode;
  state: CustomMeasureReturnType;
  index: number;
  parentNode: ChartOfAccountsContextItem | null;
}

export const FunctionPropertyItem = ({ formulaNode, state, parentNode, index }: Props) => {
  const { palette } = useTheme();
  const formulaContainerRef = useRef<HTMLDivElement>(null);
  const id = getFunctionPropertyItemUniqueId(index, formulaNode);

  const isSelectedParameter = useMemo(() => {
    if (state.selectedFormulaParameter === undefined) return false;

    return formulaNode.linkedNodes.some((ln) => findNodeByTokenData(ln, state.selectedFormulaParameter));
  }, [state.selectedFormulaParameter, formulaNode]);

  useEffect(() => {
    if (isSelectedParameter) {
      formulaContainerRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [isSelectedParameter]);

  const { root, onSelect, appendChild, getNode } = useChartOfAccountsContext();

  const currentNode = useMemo(() => {
    return getNode(id, parentNode);
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, [parentNode, id, root]);

  useEffect(() => {
    if (!parentNode) return;
    appendChild(id, parentNode);
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, [id, parentNode]);

  return (
    <PropertyItemContainer
      sx={{
        bgcolor: palette.background.grey,
        pb: 1,
        ...(formulaNode.highlighted === true ? { border: `1px solid ${palette.primary.main}` } : {}),
      }}
      ref={formulaContainerRef}
    >
      <Grid
        container
        sx={(theme) => ({
          display: "flex",
          px: 1,
          gap: 1.25,
          height: "28px",
          bgcolor: currentNode?.checked
            ? makeTintedBackgroundFromColor(theme, theme.palette.primary.main)
            : theme.palette.background.paper,
          borderTopLeftRadius: "4px",
          borderTopRightRadius: "4px",
          borderBottom: `1px solid ${theme.palette.divider}`,
          alignItems: "center",
          alignContent: "center",
          width: "100%",
        })}
      >
        {currentNode && (
          <Checkbox
            indeterminate={currentNode.indeterminate}
            checked={currentNode.checked}
            sx={{ width: "28px", height: "28px" }}
            onChange={(e) => {
              onSelect(currentNode?.identifier, e.target.checked);
            }}
          />
        )}
        <PropertyName text={formulaNode.text} />
      </Grid>
      <Stack component="ul" className="formulaTree" sx={{ flex: 1, width: "100%" }}>
        {currentNode &&
          formulaNode.args?.map((arg, i) => {
            return (
              <VariablePropertyItem
                // eslint-disable-next-line @eslint-react/no-array-index-key
                key={`${arg.key}_${i}`}
                formulaNode={arg}
                state={state}
                sx={{ mt: 1.5, flex: 1, width: "auto" }}
                parentNode={currentNode}
                index={i}
              />
            );
          })}
      </Stack>
    </PropertyItemContainer>
  );
};

export default FunctionPropertyItem;
